import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, Divider, CardMedia, Grid, Button, IconButton, Icon, Tooltip } from '@mui/material';
import styled from '@emotion/styled';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import SellRoundedIcon from '@mui/icons-material/SellRounded';
import AccessTime from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import ShoppingCartCheckoutRoundedIcon from '@mui/icons-material/ShoppingCartCheckoutRounded';
import Face2RoundedIcon from '@mui/icons-material/Face2Rounded';
import Add from '@mui/icons-material/Add';
import Remove from '@mui/icons-material/Remove';
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded';
import SubdirectoryArrowRightRoundedIcon from '@mui/icons-material/SubdirectoryArrowRightRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';

import useSnackbarStore from '../../../store/snackbar';

import { formatGenericDate, formatCurrency } from '../../../utils/util';

import { SectionHeaderBar, BasicFlex, LabelUpper, autoCompleteDisplayLabel, DotSeparator, DialogContent } from '../../../utils/Common';

import { useFormikContext, FieldArray } from 'formik';

import { useFormData } from '../../../contexts/FormData';

import { NewValueContext } from '../../../contexts/NewValue';

import Field from '../../../utils/FormikFields/Field';

import AutoCompleteArray from '../../../utils/FormikFields/AutoCompleteArray';

import Dialog from '../../../utils/utilities/Dialog';

import useRepairsStore from '../../../store/repairs';

import EditRepairTicket from '../../Repair_Tickets/EditRepairTicket';

import AddRepairs from './AddRepairs';

import EditInventory from '../../Inventory/EditInventory';

import useIsMobile from '../../../hooks/useIsMobile';

import { deleteTask } from '../../Repair_Tickets/apiService';

import { useMutation, useQueryClient } from '@tanstack/react-query';

// linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);

const Container = styled.div`
  background-color: ${props => props.theme.colors.softBg};
  border-radius: 5px;
  padding: .7rem 1rem;
`

const SearchContainer = styled.div`
  margin: .5rem 0;
`
const InventoryListContainer = styled(BasicFlex)`
  margin-top: 1rem;
  width: 100%;
  gap: 1rem;
`;

const StyledCard = styled(Card)`
 border-radius: 8px;
 border: 1px solid ${props => props.theme.colors.border};
 box-shadow: 0 1px 5px rgba(0, 0, 0, 0.12);
 transition: all 0.2s ease-in-out;
 overflow: hidden;
 
 &:hover {
   border-color: ${props => props.theme.colors.darkBg};
   box-shadow: 0 2px 8px rgba(25, 118, 210, 0.1);
 }
`;

const ImageContainer = styled(BasicFlex)`
  background-image: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
  border-radius: 5px;
  border-right: 1px solid ${props => props.theme.colors.border};
  min-height: 8rem;
  width: 8rem;
  justify-content: center;
  align-items: center;
`;

const ServiceImage = styled('img')`
 width: 100%;
 height: 100%;
 object-fit: cover;
`;

const ServiceItemDetails = styled(BasicFlex)`
  min-width: fit-content;
  width: auto;
  margin-right: 4rem;
`;

const ServiceTitle = styled.div`
  text-transform: uppercase;
  font-weight: 600;
  font-size: 1.4rem;
  /* color: ${props => props.theme.colors.mildText}; */
  /* margin-bottom: .5rem; */
`

const DetailLineStyles = styled.div`
  /* font-size: 1.4rem; */
  color: ${props => props.theme.colors.mildText};
`;

const PriceDisplay = styled.div`
  height: min-content;

  .MuiInputBase-input {
    padding: .2rem !important;
  }
`

const ServiceActions = styled(BasicFlex)`
  flex-wrap: wrap;

  @media (${props => props.theme.breaks.down('md')}) {
    margin-top: 1rem;
  }
`;

const AttachedRepairs = styled.div`
  /* padding: .5rem; */
  margin-top: 1.2rem;
`

const AttachedRepairsList = styled(BasicFlex)`
  margin-top: .8rem;
  gap: .6rem;
`;

const RepairLineStyles = styled(BasicFlex)`
  color: ${props => props.theme.colors.mildText};
  font-weight: 500;
  margin-bottom: .3rem;

  .MuiInputBase-input {
    padding: .2rem !important;
  }
`


const displayLabel = (option) => {
  let status;
  if (option?.status === "sold") {
    status = `Sold On ${formatGenericDate(option?.last_log_date, "short")} To ${option?.customer_name}`
  } else if (option?.status === "removed") {
    status = "Returned";
  } else {
    status = "In Stock"
  }
  let info = <div><span style={{fontWeight: "600"}}>{option?.brand_name}</span> &middot; {option?.sku} &middot; <span style={{fontStyle: "italic"}}>{status}</span></div>
  return autoCompleteDisplayLabel(option?.wig_id, info);
}

const DetailLine = ({ Icon, value}) => {
  return (
    <DetailLineStyles justify="flex-start" gap="1rem">
      {Icon ? <Icon fontSize='small' color="icon" /> : null}
      {value}
    </DetailLineStyles>
  )
}

const RemoveRepair = ({ repair }) => {
  const { values } = useFormikContext() || {};
  const invoice_id = values?.invoice_id;
  const rti = repair?.repair_task_id;

  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const queryClient = useQueryClient();
  const { mutate } = useMutation({
    mutationFn: () => deleteTask(rti),
    onSuccess: () => {
      showSnackbar('Removed successfully!', 'success');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['repair_tickets'] });
    },
    onError: () => showSnackbar('Failed to remove!', 'error')
  });

  const saveFunc = () => mutate();
  
  return (
     <Dialog
        title={`Delete ${repair?.name}`}
        toggle={<Tooltip title={`Delete ${repair?.name}`}><IconButton size="small"><RemoveCircleOutlineRoundedIcon color="error" /></IconButton></Tooltip>}
        size="sm"
        saveFunc={saveFunc}
      >
        <DialogContent fs="1.6rem">
          <BasicFlex align="flex-start" gap="1rem">
            <WarningAmberRoundedIcon color="warning" />
          <div>By removing this repair from the invoice, it will delete this repair from the system. Are you sure you want to continue?</div>
          </BasicFlex>
          </DialogContent>
      </Dialog>
  )
}



const RepairLine = ({ repair }) => {
  const { values } = useFormikContext() || {};
  let index = values?.repairs?.findIndex(r => r?.repair_task_id === repair?.repair_task_id);

  return (
    <RepairLineStyles justify="flex-start" gap="1rem">
      <SubdirectoryArrowRightRoundedIcon fontSize='small' color="icon" />
      <div>{repair?.name}</div>
      <div>&middot;</div> <div><Field name={`repairs.${index}.rate`} type="number" InputProps={{sx: {margin: 0, width: "8rem", paddingLeft: ".5rem !important", fontSize: "1.4rem", fontWeight: "500"}, startAdornment: <AttachMoneyRoundedIcon fontSize='small' />}} /></div>
      <RemoveRepair repair={repair} />
    </RepairLineStyles>
  )
};


const InventoryLine = ({ inventory = {}, index, remove }) => {
  const { values } = useFormikContext() || {};
  let { image, name, sku, description } = inventory;

  const { isMobile } = useIsMobile();

  let repairs = values?.repairs || [];
  repairs = repairs.filter(repair => repair?.wig_id === name);
  return (
    <StyledCard sx={{p: ".6rem !important", width: "100%"}} variant='outlined'>
      <BasicFlex justify="flex-start">
        {/* <ImageContainer align="stretch">
        {image ? (
           <ServiceImage src={image} alt={sku} />
         ) : (
           <Face2RoundedIcon sx={{ fontSize: 32, color: '#bdbdbd' }} />
         )}
        </ImageContainer> */}
        <CardContent sx={{p: ".5rem 1.6rem !important", width: "100%"}}>
         <BasicFlex justify="space-between" dir={isMobile ? "column" : "row"}>
          <ServiceItemDetails dir="column" gap=".5rem" align="flex-start">
            <ServiceTitle>{name}</ServiceTitle>
            <DetailLineStyles>{description}</DetailLineStyles>
          </ServiceItemDetails>

          <ServiceActions align="flex-end" justify={isMobile ? "flex-start" : "space-between"} gap="1rem" >
            <PriceDisplay>
             <Field name={`wigs.${index}.rate`} type="number" InputProps={{sx: {margin: 0, width: "12rem", paddingLeft: ".5rem !important", fontSize: "1.6rem", fontWeight: "500", gap: ".5rem"}, startAdornment: <AttachMoneyRoundedIcon fontSize='small' />}} />
            </PriceDisplay>
            <AddRepairs wig={{wig_id: name, description: sku || description}} />
            <BasicFlex width="8rem">
              {!repairs?.length ? <Button size="small" color="basic" startIcon={<DeleteForeverRoundedIcon />} onClick={() => remove(index)}>
                Remove
              </Button> : null}
              
            </BasicFlex>

          </ServiceActions>
         </BasicFlex>

         {repairs?.length ? <AttachedRepairs>
          <LabelUpper $normal $size="1rem" $light>Added Repairs</LabelUpper>
          <AttachedRepairsList dir="column">
            {repairs?.map((repair, index) => {
              return (<RepairLine key={repair?.name} repair={repair} />)
            })}
            {/* <RepairLine repair={{name: "Root Touch-Up", rate: 45}} /> */}
          </AttachedRepairsList>
         </AttachedRepairs> : null}

        </CardContent>
      </BasicFlex>
      
    </StyledCard>
  )
}

const Inventory = () => {
  const { values, setFieldValue } = useFormikContext() || {};

  const [ newValue, addNewValue ] = useState();

  useEffect(() => {
    if (newValue) {
      let id = newValue?.inventory_id?.toString();
      let index = values?.wigs?.findIndex(wig => wig?.inventory_id?.toString() === id);
      let item = {
        inventory_id: newValue?.inventory_id,
        rate: newValue?.total_price,
        name: newValue?.wig_id,
        description: newValue?.sku,
        brand_name: newValue?.brand_name,
        item_type: "inventory",
        quantity: 1
      }
      if (index === -1) {
        setFieldValue("wigs", [...values?.wigs, item]);
      }
    }
  }, [newValue]);

  let updates = [
    {name: "inventory_id", field: "inventory_id"},
    {name: "rate", field: "total_price"},
    {name: "name", field: "wig_id"},
    {name: "description", field: "sku"},
    {name: "brand_name", field: "brand_name"},
  ];
  return (
    <NewValueContext.Provider value={{ newValue, addNewValue }}>
    <Grid size={{xs: 12}} sx={{ marginTop: "1.5rem"}}>
       <Container>
        <BasicFlex>
          <LabelUpper>Wigs</LabelUpper>
          <Dialog
            title="Add New Inventory"
            toggle={<Button color="info" variant="text" sx={{py: "0", fontSize: "1.2rem"}}>Add New Inventory</Button>}
            size="xl"
            useForm={true}
          >
            <EditInventory dialog ignore_id={true} />
          </Dialog>
        </BasicFlex>
        
          <FieldArray name="wigs">
            {({ push, remove }) => (
              <>
               <SearchContainer>
                  <AutoCompleteArray fieldName="wig_id" name="add_wigs" label="Select Wigs" url="inventory" placeholder='Add Wigs' push={push} 
                  additionalFields={{quantity: 1, item_type: "inventory"}} formUpdates={updates} displayLabel={displayLabel} disabled={!values?.customer_id}
                  server={true}
                  conditions={`&status[$ne]=sold`}
                  />
               </SearchContainer>
                {values.wigs.length ? <InventoryListContainer dir="column">
                { values.wigs.map((wig, index) => (
                    <InventoryLine key={index} inventory={wig} index={index} remove={remove} />
                  ))}
                </InventoryListContainer> : null}
              </>
            )}
          </FieldArray>
      </Container>
    </Grid>
    </NewValueContext.Provider>
  )
};

export default Inventory;